/* Volvik Custom Featured Collection
   Keep Ignite's native product-card / swatch behavior intact.
   Custom behavior:
   1) persistent swatch selection
   2) controls only when there is a REAL visible overflow
   3) smooth one-card navigation on custom arrows
*/

(() => {
  const SECTION_SELECTOR = '.custom-featured-collection';
  const GRID_SELECTOR = '.custom-featured-collection__grid';
  const SLIDE_SELECTOR = '.custom-featured-collection__slide';
  const CONTROLS_SELECTOR = '[data-cfc-controls]';
  const SWATCH_SELECTOR = '.card-swatch-container button.swatch';
  const SWATCH_HANDOFF_CLASS = 'is-swatch-preview-active';

  function getCard(element) {
    return element?.closest('product-card') || null;
  }

  function getPrimaryImage(card) {
    if (!card) return null;

    return (
      Array.from(card.querySelectorAll('.card__media .media img')).find(
        (image) =>
          !image.classList.contains('swatch-hover-image') &&
          !image.classList.contains('card__image-hover')
      ) || null
    );
  }

  function selectSwatch(swatch, changeImage = true) {
    const card = getCard(swatch);
    if (!card) return;

    card.querySelectorAll(SWATCH_SELECTOR).forEach((item) => {
      const selected = item === swatch;

      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    if (!changeImage) return;

    const imageUrl = swatch.dataset.variantMedia;
    const primaryImage = getPrimaryImage(card);

    if (!imageUrl || !primaryImage) return;

    primaryImage.src = imageUrl;
    primaryImage.srcset = imageUrl;
    card.classList.add(SWATCH_HANDOFF_CLASS);
  }

  function initCard(card) {
    if (!card || card.dataset.volvikSwatchCardReady === 'true') return;

    const swatches = card.querySelectorAll(SWATCH_SELECTOR);
    if (!swatches.length) return;

    card.dataset.volvikSwatchCardReady = 'true';

    card.addEventListener(
      'mouseleave',
      () => {
        if (!card.classList.contains(SWATCH_HANDOFF_CLASS)) return;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            card.classList.remove(SWATCH_HANDOFF_CLASS);
          });
        });
      },
      { passive: true }
    );

    swatches.forEach((swatch) => {
      if (!swatch.hasAttribute('aria-pressed')) {
        swatch.setAttribute('aria-pressed', 'false');
      }
    });

    const selected = card.querySelector(
      `${SWATCH_SELECTOR}.is-selected, ${SWATCH_SELECTOR}[aria-pressed="true"]`
    );

    if (!selected) {
      selectSwatch(swatches[0], false);
    }
  }

  function initCards(section) {
    section.querySelectorAll('product-card').forEach(initCard);
  }

  function getVisibleSlides(grid) {
    if (!grid) return [];

    return Array.from(grid.querySelectorAll(SLIDE_SELECTOR)).filter(
      (slide) => slide.offsetWidth > 0
    );
  }

  /*
    IMPORTANT:
    Do not use scrollWidth to decide whether arrows are needed.

    Ignite and our custom slider can have extra scrollable width from layout,
    pseudo-elements or slider geometry even when every real card is fully visible.

    Instead, measure the ACTUAL span occupied by the product cards.
  */
  function getSliderMetrics(grid) {
    const slides = getVisibleSlides(grid);

    if (!grid || !slides.length) {
      return {
        slides,
        contentSpan: 0,
        viewportWidth: 0,
        maxScroll: 0,
        hasRealOverflow: false,
        step: 0,
      };
    }

    const first = slides[0];
    const last = slides[slides.length - 1];

    const contentStart = first.offsetLeft;
    const contentEnd = last.offsetLeft + last.offsetWidth;
    const contentSpan = Math.max(0, contentEnd - contentStart);
    const viewportWidth = grid.clientWidth;

    /*
      A 4px tolerance prevents sub-pixel rounding from creating fake arrows
      when four cards actually fit.
    */
    const hasRealOverflow = contentSpan > viewportWidth + 4;

    let step = 0;

    if (slides.length > 1) {
      step = slides[1].offsetLeft - slides[0].offsetLeft;
    }

    if (!step && first.offsetWidth) {
      step = first.offsetWidth;
    }

    const maxScroll = Math.max(0, contentSpan - viewportWidth);

    return {
      slides,
      contentSpan,
      viewportWidth,
      maxScroll,
      hasRealOverflow,
      step,
    };
  }

  function syncSliderControls(section) {
    if (!section) return;

    const grid = section.querySelector(GRID_SELECTOR);
    const controls = section.querySelector(CONTROLS_SELECTOR);

    if (!grid || !controls) return;

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    const controlsEnabledAtBreakpoint = isDesktop
      ? controls.classList.contains(
          'custom-featured-collection__controls--desktop'
        )
      : controls.classList.contains(
          'custom-featured-collection__controls--mobile'
        );

    const metrics = getSliderMetrics(grid);

    const shouldShow =
      controlsEnabledAtBreakpoint && metrics.hasRealOverflow;

    /*
      Use both hidden AND inline display.
      The section CSS has explicit display:flex rules for the controls,
      so the inline display:none makes the result deterministic.
    */
    controls.hidden = !shouldShow;
    controls.style.display = shouldShow ? '' : 'none';
    controls.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

    if (!metrics.hasRealOverflow && grid.scrollLeft !== 0) {
      grid.scrollTo({
        left: 0,
        behavior: 'auto',
      });
    }
  }

  function smoothScrollByOneCard(section, button) {
    const grid = section.querySelector(GRID_SELECTOR);
    if (!grid || !button) return;

    const metrics = getSliderMetrics(grid);
    if (!metrics.hasRealOverflow || !metrics.step) return;

    const direction = button.name === 'previous' ? -1 : 1;

    const current = grid.scrollLeft;
    const rawTarget = current + direction * metrics.step;

    const target = Math.min(
      metrics.maxScroll,
      Math.max(0, rawTarget)
    );

    /*
      Explicit smooth behavior avoids the abrupt jump that happened at
      intermediate desktop widths. We still move exactly one native card step.
    */
    grid.scrollTo({
      left: target,
      behavior: 'smooth',
    });
  }

  let recalcTimer;

  function nudgeSliderRecalc(section) {
    window.clearTimeout(recalcTimer);

    recalcTimer = window.setTimeout(() => {
      /*
        Let Ignite update its own slider measurements first.
      */
      window.dispatchEvent(new Event('resize'));

      requestAnimationFrame(() => {
        syncSliderControls(section);
      });
    }, 80);
  }

  function initSection(section) {
    if (
      !section ||
      section.dataset.volvikSwatchClickReady === 'true'
    ) {
      return;
    }

    section.dataset.volvikSwatchClickReady = 'true';

    initCards(section);

    /*
      Capture arrow clicks BEFORE Ignite's slider-component receives them.

      Ignite's native slider still handles dragging, counters and scroll state.
      We only replace the arrow movement with an explicit smooth scroll because
      this custom fixed-width card layout can otherwise jump at some widths.
    */
    section.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest(
          `${CONTROLS_SELECTOR} .slider-button`
        );

        if (!button || !section.contains(button)) return;

        event.preventDefault();
        event.stopPropagation();

        smoothScrollByOneCard(section, button);
      },
      true
    );

    const mutationObserver = new MutationObserver(() => {
      initCards(section);
      nudgeSliderRecalc(section);
    });

    mutationObserver.observe(section, {
      childList: true,
      subtree: true,
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        syncSliderControls(section);
      });

      resizeObserver.observe(section);

      const grid = section.querySelector(GRID_SELECTOR);

      if (grid) {
        resizeObserver.observe(grid);
      }
    }

    window.addEventListener(
      'resize',
      () => {
        requestAnimationFrame(() => {
          syncSliderControls(section);
        });
      },
      { passive: true }
    );

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncSliderControls(section);
      });
    });

    section.addEventListener('mouseover', (event) => {
      const swatch = event.target.closest(SWATCH_SELECTOR);

      if (
        !swatch ||
        !section.contains(swatch) ||
        !swatch.dataset.variantMedia
      ) {
        return;
      }

      getCard(swatch)?.classList.add(SWATCH_HANDOFF_CLASS);
    });

    section.addEventListener('click', (event) => {
      const swatch = event.target.closest(SWATCH_SELECTOR);

      if (!swatch || !section.contains(swatch)) return;

      selectSwatch(swatch, true);
    });
  }

  function init(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      initSection(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(),
      { once: true }
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(event.target);
    }
  );
})();